<template>
  <div v-if="lineItems.length > 0" class="invoice-cost-title">Line item</div>
  <div class="invoice-cost-table">
    <table v-if="lineItems.length > 0" class="borderless-table">
      <thead>
        <tr class="invoice-cost-tr">
          <th scope="col">Name</th>
          <th scope="col">Cost per unit</th>
          <th scope="col">Quantity</th>
          <th class="align-right" scope="col">Total cost</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(lineItem, index) in lineItems"
          :key="`line-item-${lineItem.id}-${index}`"
        >
          <td v-if="lineItem.name?.length > 15">
            <NrTooltip :label="lineItem.name">
              {{ truncate(lineItem.name, { length: 40 }) }}
            </NrTooltip>
          </td>
          <td v-else>
            {{ lineItem.name }}
          </td>
          <td>{{ formatCost(lineItem.cost, 3) || '$0.00' }}</td>
          <td>{{ lineItem.quantity }}</td>
          <td class="align-right">
            {{ formatCost(lineItem.cost * lineItem.quantity) }}
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="staffHours.length > 0" class="invoice-cost-title">
      Staff hours
    </div>
    <table v-if="staffHours.length > 0" class="borderless-table">
      <tbody>
        <tr>
          <td class="invoice-bold">Name</td>
          <td class="invoice-bold">Cost per hour</td>
          <td class="invoice-bold">Quantity</td>
          <td class="align-right invoice-bold">Total cost</td>
        </tr>
        <tr v-for="(hour, index) in staffHours" :key="`hour-${index}`">
          <td>
            <p v-if="showInvoiceStaffName">
              <span v-if="hour.user.name">
                {{ hour.user.name }}
              </span>
              <span v-else>(Staff)</span>
            </p>
            <p v-else>
              <span v-if="hour.user.title"> {{ hour.user.title }} </span>
              <span v-else>(Staff)</span>
            </p>
          </td>
          <td>{{ formatCost(hour.hourly_rate) }}</td>
          <td>{{ hour.staff_hours }}</td>
          <td class="align-right">{{ formatCost(hour.staff_cost) }}</td>
        </tr>
      </tbody>
    </table>
    <table class="borderless-table">
      <tbody>
        <tr v-if="lineItems.length > 0 || staffHours.length > 0">
          <td class="invoice-bold">Subtotal</td>
          <td />
          <td />
          <td class="align-right invoice-bold">
            {{ formatCost(subtotalInvoiceAmount) }}
          </td>
        </tr>
        <tr
          v-for="(surcharge, index) in surcharges"
          :key="`surcharge-${index}`"
        >
          <td />
          <td />
          <td>{{ setSurchargeName(surcharge) }}</td>
          <td class="align-right">{{ formatSurcharge(surcharge) }}</td>
        </tr>
        <tr>
          <td />
          <td />
          <td />
          <td class="align-right invoice-bold">
            Total: {{ formatCost(totalAmount / 100) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import truncate from 'lodash/truncate'
import helpers from '../../../utils/helpers.js'
import { mapState } from 'vuex'

interface LineItemOption {
  name: string
  cost: number
  quantity: number
  description: string
}

interface SurchargeOption {
  name: string
  amount: number
  cost_type: string // eslint-disable-line camelcase
  description: string
}

interface StaffHourOption {
  name: string
  hourly_rate: number // eslint-disable-line camelcase
  staff_cost: number // eslint-disable-line camelcase
  staff_hours: string // eslint-disable-line camelcase
}

export default {
  components: {
    NrTooltip
  },

  mounted() {
    this.calculateLineItems(this.lineItems)
    this.calculateStaffHours(this.staffHours)
  },

  computed: {
    ...mapState(['account', 'currentUser']),

    showInvoiceStaffName() {
      return this.account.show_invoice_staff_name
    }
  },

  methods: {
    truncate,

    setSurchargeName(surcharge: SurchargeOption): string {
      return surcharge.cost_type === 'percentage'
        ? `${surcharge?.name} (${surcharge?.amount}%)`
        : `${surcharge?.name}`
    },

    formatSurcharge(surcharge: SurchargeOption): number {
      const amount = surcharge?.amount
      return surcharge.cost_type === 'percentage'
        ? this.formatCost((amount / 100) * this.subtotalInvoiceAmount)
        : this.formatCost(Number(amount))
    },

    formatCost(cost: number, maximumFractionDigits: number = 2): string {
      return helpers.formatCost(cost, maximumFractionDigits)
    },

    calculateLineItems(lineItems: Array<LineItemOption>): void {
      lineItems.forEach((lineItem: LineItemOption) => {
        const cost = Number(lineItem.cost) * lineItem.quantity
        this.subtotalInvoiceAmount +=
          Math.round((cost + Number.EPSILON) * 100) / 100
      })
    },

    calculateStaffHours(staffHours: Array<StaffHourOption>): void {
      staffHours.forEach((hour: StaffHourOption) => {
        this.subtotalInvoiceAmount += Number(hour.staff_cost)
      })
    }
  },

  props: {
    totalAmount: {
      type: Number,
      default() {
        return 0
      }
    },
    lineItems: {
      type: Array,
      default() {
        return []
      }
    },
    surcharges: {
      type: Array,
      default() {
        return []
      }
    },
    staffHours: {
      type: Array,
      default() {
        return []
      }
    }
  },

  data() {
    return {
      subtotalInvoiceAmount: 0
    }
  }
}
</script>
<style lang="scss">
@import '@nextrequestco/components/src/styles/tokens/variables.scss';

.invoice-cost-title {
  border-bottom: 1px solid #000;
}
.invoice-bold {
  font-weight: $font-weight-semibold;
}
.invoice-cost-table {
  overflow-x: auto;
  .borderless-table {
    min-width: 600px;
    width: 100%;
    table-layout: fixed;
    overflow-x: scroll;

    th {
      border-bottom: none;
    }
    td {
      &:nth-child(1) {
        width: 40%;
      }
      &:nth-child(2) {
        width: 15%;
      }
      &:nth-child(3) {
        width: 20%;
      }
      &:nth-child(4) {
        width: 15%;
      }
    }

    td {
      border: none;
    }
  }
}
</style>
